/* eslint-disable @typescript-eslint/no-empty-function */
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import type { UserType } from 'lib/types';
import { useRouter } from 'next/router';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { userInfo } from 'services/azure.service';

export type UserContextState = {
  user: UserType | null;
  isAuthenticated: boolean;
  inProgress: InteractionStatus;
};

const UserContext = createContext<UserContextState>({
  user: null,
  isAuthenticated: false,
  inProgress: InteractionStatus.None,
});

export function UserContextProvider({ children }) {
  const router = useRouter();
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [state, setState] = useState<UserType | null>(null);

  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None) {
      (async () => {
        try {
          const data = await userInfo();
          setState(data);
        } catch (err) {
          // empty catch
        }
      })();
    }
  }, [inProgress, instance, isAuthenticated, router]);

  const contextValue = useMemo(
    () => ({
      user: state,
      isAuthenticated,
      inProgress,
    }),
    [state, inProgress, isAuthenticated],
  );

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
}

export const useContextUser = () => {
  return useContext(UserContext);
};
